import { useState, useEffect, useRef } from 'react';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';
import { useAppDispatch } from './useAppDispatch';
import { setEvents } from '@/store/eventsSlice';
import { setCompany, setCompanyLoading, setCompanyError } from '@/store/companySlice';
import { setVersions, setVersionsLoading, setVersionsError, setCurrentSlug } from '@/store/versionsSlice';
import { setMaps, setMapsLoading, setMapsError } from '@/store/mapsSlice';
import { toast } from 'sonner';
import { setQuarterlyEvents } from '@/store/quarterlyEventsSlice';
import { setCompanyTags } from '@/store/tagsSlice';

// Create a cache for company details to avoid refetching
const companyDetailsCache = new Map<string, any>();
// Create a cache for versions to avoid refetching
const versionsCache = new Map<string, any>();

// Cache expiration time in milliseconds (30 minutes)
const CACHE_EXPIRATION = 30 * 60 * 1000;
// Cache expiration time for versions in milliseconds (15 minutes)
const VERSIONS_CACHE_EXPIRATION = 15 * 60 * 1000;

interface UseCompanyDetailsOptions {
  slug: string;
  enabled?: boolean;
  forceRefresh?: boolean;
}

export const useCompanyDetails = ({ slug, enabled = true, forceRefresh = false }: UseCompanyDetailsOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versionsLoading, setVersionsLoadingState] = useState(false);
  const [versionsError, setVersionsErrorState] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const fetchingRef = useRef(false);
  const versionsFetchingRef = useRef(false);
  const lastSlugRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !slug) return;

    // Prevent multiple simultaneous fetches
    if (fetchingRef.current) return;

    // Check if the slug has changed
    const slugChanged = lastSlugRef.current !== slug;
    
    // Always clear the data when the slug changes or forceRefresh is true
    if (slugChanged || forceRefresh) {
      console.log(`🔄 [useCompanyDetails] ${slugChanged ? `Slug changed from ${lastSlugRef.current} to ${slug}` : 'Force refresh requested'}, clearing cached data`);
      
      // Clear Redux store data to prevent showing stale data
      dispatch(setCompany(null));
      dispatch(setMaps([]));
      dispatch(setVersions([]));
      dispatch(setEvents([]));
      dispatch(setCompanyTags([]));
      
      // If forceRefresh is true, also clear the cache for this slug
      if (forceRefresh) {
        console.log(`🔄 [useCompanyDetails] Force refresh: removing ${slug} from cache`);
        companyDetailsCache.delete(slug);
        versionsCache.delete(slug);
      }
    } else if (lastSlugRef.current === slug && !forceRefresh) {
      // Prevent refetching for the same slug unless forceRefresh is true
      return;
    }

    lastSlugRef.current = slug;
    
    // Create an AbortController for this request
    const abortController = new AbortController();

    const fetchCompanyDetails = async () => {
      setLoading(true);
      setError(null);
      dispatch(setCompanyLoading(true));
      fetchingRef.current = true;
      
      // Set a timeout to show a loading message if it takes too long
      const loadingMessageTimeout = setTimeout(() => {
        console.log('⏳ [useCompanyDetails] Loading is taking longer than expected...');
        // toast.info('Loading company details...', {
        //   description: 'This is taking longer than expected. Please wait...',
        //   duration: 5000,
        // });
      }, 5000);

      try {
        // Get the response (either from cache or fresh)
        let response;

        // We've already checked for slug changes and forceRefresh at the beginning of the effect
        // Here we just need to check if we can use the cache
        
        // Check if we have cached data for this company and forceRefresh is false
        if (!forceRefresh && companyDetailsCache.has(slug)) {
          const cachedData = companyDetailsCache.get(slug);

          // Check if the cache is still valid
          if (cachedData && cachedData.timestamp && (Date.now() - cachedData.timestamp) < CACHE_EXPIRATION) {
            response = cachedData.data;
            console.log(`📦 [useCompanyDetails] Using cached company data for ${slug}`);
          } else {
            // Cache expired, fetch fresh data
            console.log(`📦 [useCompanyDetails] Cache expired, fetching fresh data for ${slug}`);
            response = await invokeEdgeFunction('get-company-details', { slug, forceRefresh: true });

            // Update the cache with fresh data
            companyDetailsCache.set(slug, {
              data: response,
              timestamp: Date.now()
            });
          }
        } else {
          // Force refresh or no cache available
          if (forceRefresh) {
            console.log(`🔄 [useCompanyDetails] Force refreshing data for ${slug}`);
            // When forcing refresh, pass forceRefresh to the edge function
            response = await invokeEdgeFunction('get-company-details', { slug, forceRefresh: true });
          } else {
            console.log(`📦 [useCompanyDetails] No cached data, fetching for ${slug}`);
            response = await invokeEdgeFunction('get-company-details', { slug });
          }
          console.log('📦 [useCompanyDetails] Company data response:', response);


          // Cache the response for future use
          companyDetailsCache.set(slug, {
            data: response,
            timestamp: Date.now()
          });

          console.log(`📦 [useCompanyDetails] Company data for ${slug}:`, response);
        }

        if (response.error) {
          throw new Error(response.error);
        }

        // Update company in Redux store
        if (response.data && response.data.company) {
          // Log the company data to see what's coming from the API
          console.log('📊 [useCompanyDetails] Company data from API:', response.data.company);

          // Check if pages have pricing_metrics
          if (response.data.company.pages && response.data.company.pages.length > 0) {
            console.log('📊 [useCompanyDetails] First page pricing_metrics:',
              response.data.company.pages[0].pricing_metrics ?
              `Found ${response.data.company.pages[0].pricing_metrics.length} metrics` :
              'No metrics found');
          }

          dispatch(setCompany(response.data.company));

          // Clear events since we're not fetching them anymore
          dispatch(setEvents([]));

          // Use tags directly from the company_data view
          if (response.data.company.tags && Array.isArray(response.data.company.tags)) {
            // Update the tags store with tags from company_data view
            dispatch(setCompanyTags(response.data.company.tags));
          } else {
            dispatch(setCompanyTags([]));
          }
          
          // Store maps data in Redux if available
          if (response.data.maps && Array.isArray(response.data.maps)) {
            console.log('📊 [useCompanyDetails] Maps data from API:', response.data.maps);
           
            dispatch(setMaps(response.data.maps));
          } else {
            console.log('📊 [useCompanyDetails] No maps data found');
            dispatch(setMaps([]));
          }
        } else if (response.company) {
          // Log the company data to see what's coming from the API
          console.log('📊 [useCompanyDetails] Company data from API (legacy format):', response.company);

          console.log(response);
          // Check if pages have pricing_metrics
          if (response.company.pages && response.company.pages.length > 0) {
            console.log('📊 [useCompanyDetails] First page pricing_metrics (legacy format):',
              response.company.pages[0].pricing_metrics ?
              `Found ${response.company.pages[0].pricing_metrics.length} metrics` :
              'No metrics found');
          }

          // Handle direct company property (for backward compatibility)
          dispatch(setCompany(response.company));

          // Clear events since we're not fetching them anymore
          dispatch(setEvents([]));

          // Handle tags directly from the company_data view format
          if (response.company.tags && Array.isArray(response.company.tags)) {
            dispatch(setCompanyTags(response.company.tags));
          } else {
            dispatch(setCompanyTags([]));
          }
          
          // Store maps data in Redux if available (legacy format)
          if (response.maps && Array.isArray(response.maps)) {
            console.log('📊 [useCompanyDetails] Maps data from API (legacy format):', response.maps);
            dispatch(setMaps(response.maps));
          } else {
            console.log('📊 [useCompanyDetails] No maps data found in legacy format');
            dispatch(setMaps([]));
          }
        } else {
          // Create a minimal company object with the slug to avoid errors
          dispatch(setCompany({
            slug: slug,
            logoUrl: '',
            employees: '',
            category: '',
            description: 'No company data available',
            url: '',
            legacySlug: slug,
            pricingMetrics: [],
            pages: []
          }));
          dispatch(setEvents([]));
          dispatch(setMaps([]));
        }

        // Fetch versions immediately after company details
        console.log("Fetching versions!!!");
        fetchVersions();
        
        // If there's a selected version in Redux, keep it, otherwise it will be set to the latest version

        setLoading(false);
        return response;
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch company details';
        setError(errorMessage);

        // Create a minimal company object with the slug to avoid errors
        dispatch(setCompany({
          slug: slug,
          logoUrl: '',
          employees: '',
          category: '',
          description: 'Company data not available',
          url: '',
          legacySlug: slug,
          pricingMetrics: [],
          pages: []
        }));

        dispatch(setCompanyError(errorMessage));

        // Clear events, tags, and maps to prevent stale data
        dispatch(setEvents([]));
        dispatch(setCompanyTags([]));
        dispatch(setMaps([]));

        toast.error('Failed to load company details', {
          description: errorMessage || 'Please try again later',
        });
        return null;
      } finally {
        setLoading(false);
        dispatch(setCompanyLoading(false));
        fetchingRef.current = false;
      }
    };

    const fetchVersions = async () => {
      // Prevent multiple simultaneous fetches
      if (versionsFetchingRef.current) return;

      setVersionsLoadingState(true);
      setVersionsErrorState(null);
      dispatch(setVersionsLoading(true));
      dispatch(setCurrentSlug(slug));
      versionsFetchingRef.current = true;

      try {
        // Get the response (either from cache or fresh)
        let response;

        // We've already checked for slug changes and forceRefresh at the beginning of the effect
        // Here we just need to check if we can use the cache
        
        // Check if we have cached data for this slug and forceRefresh is false
        if (!forceRefresh && versionsCache.has(slug)) {
          const cachedData = versionsCache.get(slug);

          // Check if the cache is still valid
          if (cachedData && cachedData.timestamp && (Date.now() - cachedData.timestamp) < VERSIONS_CACHE_EXPIRATION) {
            response = cachedData.data;
            console.log(`📦 [useCompanyDetails] Using cached versions data for ${slug}`);
          } else {
            // Cache expired, fetch fresh data
            console.log(`📦 [useCompanyDetails] Cache expired, fetching fresh versions data for ${slug}`);
            response = await invokeEdgeFunction('get-versions', { slug, forceRefresh: true });

            // Update the cache with fresh data
            versionsCache.set(slug, {
              data: response,
              timestamp: Date.now()
            });
          }
        } else {
          // Force refresh or no cache available
          if (forceRefresh) {
            console.log(`🔄 [useCompanyDetails] Force refreshing versions data for ${slug}`);
            // When forcing refresh, pass forceRefresh to the edge function
            response = await invokeEdgeFunction('get-versions', { slug, forceRefresh: true });
          } else {
            console.log(`📦 [useCompanyDetails] No cached versions data, fetching for ${slug}`);
            response = await invokeEdgeFunction('get-versions', { slug });
          }
          console.log('📦 [useCompanyDetails] Versions response:', response);
          // Log the structure of the response to debug the format
          console.log('📦 [useCompanyDetails] Response structure:', {
            hasData: !!response.data,
            hasVersionsInData: !!(response.data && response.data.versions),
            hasVersionsDirectly: !!response.versions,
            dataVersionsIsArray: !!(response.data && response.data.versions && Array.isArray(response.data.versions)),
            versionsIsArray: !!(response.versions && Array.isArray(response.versions))
          });

          // Cache the response for future use
          versionsCache.set(slug, {
            data: response,
            timestamp: Date.now()
          });
        }

        if (response.error) {
          throw new Error(response.error);
        }

        // Update versions in Redux store
        if (response.data && response.data.versions && Array.isArray(response.data.versions)) {
          console.log(`📊 [useCompanyDetails] Versions data from API for ${slug}:`, response.data.versions);
          dispatch(setVersions(response.data.versions));
        } else if (response.versions && Array.isArray(response.versions)) {
          // Fallback for backward compatibility
          console.log(`📊 [useCompanyDetails] Versions data from API (legacy format) for ${slug}:`, response.versions);
          dispatch(setVersions(response.versions));
        } else {
          // If no versions returned, set empty array
          console.log(`📊 [useCompanyDetails] No versions found for ${slug}`);
          dispatch(setVersions([]));
        }

        return (response.data && response.data.versions) || response.versions || [];
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch versions';
        setVersionsErrorState(errorMessage);
        dispatch(setVersionsError(errorMessage));

        toast.error('Failed to load versions', {
          description: errorMessage || 'Please try again later',
        });
        return [];
      } finally {
        setVersionsLoadingState(false);
        dispatch(setVersionsLoading(false));
        versionsFetchingRef.current = false;
      }
    };

    // Start the fetch process
    const fetchPromise = fetchCompanyDetails();
    
    // Cleanup function to handle component unmount or slug change
    return () => {
      // Reset the fetching flags if the component unmounts or the dependencies change
      fetchingRef.current = false;
      versionsFetchingRef.current = false;
    };
  }, [slug, enabled, forceRefresh, dispatch]);

  return { 
    loading, 
    error,
    versionsLoading,
    versionsError
  };
};
